/**
 * Auto Load History Page
 *
 * View history of all API auto load tasks
 */

import { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Drawer,
  Descriptions,
  Select,
  Input,
  Tooltip,
} from 'antd'
import {
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  RocketOutlined,
  ApiOutlined,
} from '@ant-design/icons'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import dayjs from 'dayjs'
import api from '@/services/api'

const { Text, Title } = Typography

interface AutoLoadHistoryItem {
  task_id: string
  crm_id: number
  project_name: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  total_urls: number
  auto_approved: number
  skipped: number
  failed: number
  // Moderation counters
  operator_approved: number
  operator_rejected: number
  pending_moderation: number
  created_at: string
  completed_at: string | null
}

interface AutoLoadUrlResult {
  url: string
  domain: string
  status: string
  ai_score: number | null
  ai_decision: string | null
  phones_input: string[]
  phones_extracted: string[]
  vdl_uploaded: boolean
  error: string | null
  // Extended metadata
  external_metadata: Record<string, unknown> | null
  ai_analysis_metadata: Record<string, unknown> | null
  processing_metadata: Record<string, unknown> | null
  source_id: number | null
  moderation_status: string | null
  moderation_at: string | null
  moderation_operator_id: number | null
}

interface AutoLoadTaskDetails {
  task_id: string
  crm_id: number
  status: string
  progress_percent: number
  total_urls: number
  processed_urls: number
  auto_approved: number
  skipped: number
  failed: number
  // Moderation counters
  operator_approved: number
  operator_rejected: number
  pending_moderation: number
  results: AutoLoadUrlResult[] | null
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

const statusColors: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
}

const statusIcons: Record<string, any> = {
  pending: <ClockCircleOutlined />,
  running: <RocketOutlined spin />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
}

const statusLabels: Record<string, string> = {
  pending: 'Ожидает',
  running: 'Выполняется',
  completed: 'Завершено',
  failed: 'Ошибка',
}

export default function AutoLoadHistoryPage() {
  const [items, setItems] = useState<AutoLoadHistoryItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ page: 1, per_page: 20, total: 0 })
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [crmFilter, setCrmFilter] = useState<number | undefined>()

  const [selectedTask, setSelectedTask] = useState<AutoLoadTaskDetails | null>(null)
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)

  useEffect(() => {
    loadHistory()
  }, [pagination.page, pagination.per_page, statusFilter, crmFilter])

  const loadHistory = async () => {
    try {
      setLoading(true)
      const data = await api.getAutoLoadHistory({
        page: pagination.page,
        per_page: pagination.per_page,
        status: statusFilter,
        crm_id: crmFilter,
      })
      setItems(data.items || [])
      setPagination(prev => ({ ...prev, total: data.total || 0 }))
    } catch (error: any) {
      console.error('Error loading auto load history:', error)
      message.error('Ошибка загрузки истории')
    } finally {
      setLoading(false)
    }
  }

  const loadTaskDetails = async (taskId: string) => {
    try {
      setLoadingDetails(true)
      setDrawerVisible(true)
      const data = await api.getAutoLoadTaskDetails(taskId)
      setSelectedTask(data)
    } catch (error: any) {
      console.error('Error loading task details:', error)
      message.error('Ошибка загрузки деталей задачи')
    } finally {
      setLoadingDetails(false)
    }
  }

  const handleTableChange = (pag: TablePaginationConfig) => {
    setPagination(prev => ({
      ...prev,
      page: pag.current || 1,
      per_page: pag.pageSize || 20,
    }))
  }

  const columns: ColumnsType<AutoLoadHistoryItem> = [
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag icon={statusIcons[status]} color={statusColors[status]}>
          {statusLabels[status] || status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Проект',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
      render: (name: string, record: AutoLoadHistoryItem) => (
        <div>
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            CRM ID: {record.crm_id}
          </Text>
        </div>
      ),
    },
    {
      title: 'URLs',
      dataIndex: 'total_urls',
      key: 'total_urls',
      width: 80,
      align: 'center',
    },
    {
      title: 'AI результат',
      key: 'result',
      width: 180,
      render: (_: any, record: AutoLoadHistoryItem) => (
        <Space size={4}>
          <Tag color="green">{record.auto_approved} AI+</Tag>
          <Tag color="orange">{record.skipped} пропущено</Tag>
          {record.failed > 0 && (
            <Tag color="red">{record.failed} ошибок</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Модерация',
      key: 'moderation',
      width: 180,
      render: (_: any, record: AutoLoadHistoryItem) => {
        const total = (record.operator_approved || 0) + (record.operator_rejected || 0) + (record.pending_moderation || 0)
        if (total === 0) {
          return <Text type="secondary">—</Text>
        }
        return (
          <Space size={4}>
            {record.operator_approved > 0 && (
              <Tag color="green">{record.operator_approved} принято</Tag>
            )}
            {record.operator_rejected > 0 && (
              <Tag color="red">{record.operator_rejected} откл.</Tag>
            )}
            {record.pending_moderation > 0 && (
              <Tag color="blue">{record.pending_moderation} ожид.</Tag>
            )}
          </Space>
        )
      },
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => (
        <Text style={{ fontSize: 12 }}>
          {dayjs(date).format('DD.MM.YYYY HH:mm')}
        </Text>
      ),
    },
    {
      title: 'Task ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 120,
      ellipsis: true,
      render: (taskId: string) => (
        <Text copyable style={{ fontSize: 11, fontFamily: 'monospace' }}>
          {taskId.substring(0, 8)}...
        </Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: AutoLoadHistoryItem) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => loadTaskDetails(record.task_id)}
        >
          Детали
        </Button>
      ),
    },
  ]

  const urlResultColumns: ColumnsType<AutoLoadUrlResult> = [
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      ellipsis: true,
      render: (url: string) => (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
          {url}
        </a>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const colors: Record<string, string> = {
          approved: 'green',
          skipped: 'orange',
          failed: 'red',
          pending: 'default',
        }
        return <Tag color={colors[status] || 'default'}>{status}</Tag>
      },
    },
    {
      title: 'AI Score',
      dataIndex: 'ai_score',
      key: 'ai_score',
      width: 80,
      align: 'center',
      render: (score: number | null, record: AutoLoadUrlResult) => {
        if (score === null) return '-'

        // Format AI analysis metadata for tooltip
        const aiMetadata = record.ai_analysis_metadata as any
        const tooltipContent = aiMetadata ? (
          <div style={{ maxWidth: 400 }}>
            <div><strong>Ответ нейронки:</strong></div>
            {aiMetadata.conclusion && (
              <div style={{ marginTop: 8 }}>
                <strong>Заключение:</strong> {String(aiMetadata.conclusion)}
              </div>
            )}
            {aiMetadata.thematic_relevance !== undefined && (
              <div style={{ marginTop: 4 }}>
                <strong>Тематическая релевантность:</strong> {String(aiMetadata.thematic_relevance)}/10
              </div>
            )}
            {aiMetadata.detected_business_type && (
              <div style={{ marginTop: 4 }}>
                <strong>Тип бизнеса:</strong> {String(aiMetadata.detected_business_type)}
              </div>
            )}
            {aiMetadata.detected_keywords && Array.isArray(aiMetadata.detected_keywords) && aiMetadata.detected_keywords.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <strong>Ключевые слова:</strong> {aiMetadata.detected_keywords.join(', ')}
              </div>
            )}
            {aiMetadata.phone_quality && (
              <div style={{ marginTop: 4 }}>
                <strong>Качество телефонов:</strong> {String(aiMetadata.phone_quality)}
              </div>
            )}
            {aiMetadata.confidence !== undefined && (
              <div style={{ marginTop: 4 }}>
                <strong>Уверенность:</strong> {String(aiMetadata.confidence)}
              </div>
            )}
            {aiMetadata.needs_manual_review !== undefined && (
              <div style={{ marginTop: 4 }}>
                <strong>Требует проверки:</strong> {aiMetadata.needs_manual_review ? 'Да' : 'Нет'}
              </div>
            )}
          </div>
        ) : 'Нет данных анализа'

        return (
          <Tooltip title={tooltipContent} placement="left">
            <Text style={{
              color: score >= 85 ? '#52c41a' : score >= 50 ? '#faad14' : '#ff4d4f',
              fontWeight: 600,
              cursor: 'help',
              textDecoration: 'underline dotted'
            }}>
              {score}%
            </Text>
          </Tooltip>
        )
      },
    },
    {
      title: 'Телефоны',
      dataIndex: 'phones_input',
      key: 'phones_input',
      width: 150,
      render: (phones: string[]) => (
        phones?.length > 0 ? (
          <Text style={{ fontSize: 11 }}>{phones.join(', ')}</Text>
        ) : '-'
      ),
    },
    {
      title: 'VDL',
      dataIndex: 'vdl_uploaded',
      key: 'vdl_uploaded',
      width: 60,
      align: 'center',
      render: (uploaded: boolean) => (
        uploaded ? (
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
        )
      ),
    },
    {
      title: 'Модерация',
      dataIndex: 'moderation_status',
      key: 'moderation_status',
      width: 120,
      render: (status: string | null) => {
        if (!status) return '-'
        const colors: Record<string, string> = {
          pending_moderation: 'blue',
          operator_approved: 'green',
          operator_rejected: 'red',
        }
        const labels: Record<string, string> = {
          pending_moderation: 'Ожидает',
          operator_approved: 'Принят',
          operator_rejected: 'Отклонён',
        }
        return <Tag color={colors[status] || 'default'}>{labels[status] || status}</Tag>
      },
    },
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={4} style={{ margin: 0 }}>
            <ApiOutlined style={{ marginRight: 8 }} />
            История API Auto Load
          </Title>
          <Space>
            <Select
              placeholder="Статус"
              allowClear
              style={{ width: 150 }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: 'Ожидает', value: 'pending' },
                { label: 'Выполняется', value: 'running' },
                { label: 'Завершено', value: 'completed' },
                { label: 'Ошибка', value: 'failed' },
              ]}
            />
            <Input
              placeholder="CRM ID"
              style={{ width: 120 }}
              type="number"
              value={crmFilter}
              onChange={(e) => setCrmFilter(e.target.value ? parseInt(e.target.value) : undefined)}
              allowClear
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={loadHistory}
              loading={loading}
            >
              Обновить
            </Button>
          </Space>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={items}
            loading={loading}
            rowKey="task_id"
            size="small"
            pagination={{
              current: pagination.page,
              pageSize: pagination.per_page,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total) => `Всего: ${total} задач`,
            }}
            onChange={handleTableChange}
          />
        </Card>
      </Space>

      {/* Task Details Drawer */}
      <Drawer
        title={
          <Space>
            <EyeOutlined />
            <span>Детали задачи Auto Load</span>
          </Space>
        }
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setSelectedTask(null)
        }}
        width={900}
      >
        {loadingDetails ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">Загрузка...</Text>
          </div>
        ) : selectedTask ? (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Task ID">
                  <Text copyable style={{ fontFamily: 'monospace' }}>
                    {selectedTask.task_id}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="CRM ID">
                  {selectedTask.crm_id}
                </Descriptions.Item>
                <Descriptions.Item label="Статус">
                  <Tag
                    icon={statusIcons[selectedTask.status]}
                    color={statusColors[selectedTask.status]}
                  >
                    {statusLabels[selectedTask.status] || selectedTask.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Прогресс">
                  {selectedTask.progress_percent}%
                </Descriptions.Item>
                <Descriptions.Item label="Всего URLs">
                  {selectedTask.total_urls}
                </Descriptions.Item>
                <Descriptions.Item label="Обработано">
                  {selectedTask.processed_urls}
                </Descriptions.Item>
                <Descriptions.Item label="AI одобрено">
                  <Text style={{ color: '#52c41a', fontWeight: 600 }}>
                    {selectedTask.auto_approved}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="AI пропущено">
                  <Text style={{ color: '#faad14', fontWeight: 600 }}>
                    {selectedTask.skipped}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Оператор принял">
                  <Text style={{ color: '#52c41a', fontWeight: 600 }}>
                    {selectedTask.operator_approved || 0}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Оператор отклонил">
                  <Text style={{ color: '#ff4d4f', fontWeight: 600 }}>
                    {selectedTask.operator_rejected || 0}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Ожидает модерации">
                  <Text style={{ color: '#1890ff', fontWeight: 600 }}>
                    {selectedTask.pending_moderation || 0}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Начало">
                  {selectedTask.started_at
                    ? dayjs(selectedTask.started_at).format('DD.MM.YYYY HH:mm:ss')
                    : '-'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Завершение">
                  {selectedTask.completed_at
                    ? dayjs(selectedTask.completed_at).format('DD.MM.YYYY HH:mm:ss')
                    : '-'
                  }
                </Descriptions.Item>
              </Descriptions>
              {selectedTask.error_message && (
                <div style={{ marginTop: 12 }}>
                  <Text type="danger">Ошибка: {selectedTask.error_message}</Text>
                </div>
              )}
            </Card>

            {selectedTask.results && selectedTask.results.length > 0 && (
              <Card size="small" title="Результаты по URLs">
                <Table
                  columns={urlResultColumns}
                  dataSource={selectedTask.results}
                  rowKey="url"
                  size="small"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            )}
          </Space>
        ) : null}
      </Drawer>
    </div>
  )
}
